import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Skeleton,
} from '@mui/material';
import { AdminActivityFeedItem } from '../../../types/admin';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';

interface ActivityFeedProps {
  activities: AdminActivityFeedItem[];
  loading?: boolean;
}

/**
 * ActivityFeed - Real-time feed of platform activities
 *
 * Displays:
 * - User signups
 * - Home creations
 * - Subscription changes
 * - Business creations
 *
 * Features:
 * - User avatars (with fallback initials)
 * - Timestamp formatting (relative time)
 * - Activity type icons and colors
 * - Scrollable list with fixed height
 * - Loading state with skeletons
 */
export default function ActivityFeed({ activities, loading }: ActivityFeedProps) {
  const getActivityIcon = (type: AdminActivityFeedItem['type']) => {
    switch (type) {
      case 'user_signup':
        return <PersonAddIcon />;
      case 'home_created':
        return <HomeWorkIcon />;
      case 'subscription_change':
        return <CreditCardIcon />;
      case 'business_created':
        return <BusinessCenterIcon />;
      default:
        return <PersonAddIcon />;
    }
  };

  const getActivityColor = (type: AdminActivityFeedItem['type']): string => {
    switch (type) {
      case 'user_signup':
        return '#1976d2';
      case 'home_created':
        return '#2e7d32';
      case 'subscription_change':
        return '#ed6c02';
      case 'business_created':
        return '#9c27b0';
      default:
        return '#757575';
    }
  };

  const getActivityTypeLabel = (type: AdminActivityFeedItem['type']): string => {
    switch (type) {
      case 'user_signup':
        return 'New User';
      case 'home_created':
        return 'Home';
      case 'subscription_change':
        return 'Subscription';
      case 'business_created':
        return 'Business';
      default:
        return 'Activity';
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
          <List>
            {[1, 2, 3, 4, 5].map((i) => (
              <ListItem key={i} sx={{ px: 0 }}>
                <ListItemAvatar>
                  <Skeleton variant="circular" width={40} height={40} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton variant="text" width="60%" />}
                  secondary={<Skeleton variant="text" width="40%" />}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Activity
          </Typography>
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">No recent activity</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Recent Activity
        </Typography>

        <List
          sx={{
            maxHeight: 500,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'background.default',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'action.hover',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'action.selected',
              },
            },
          }}
        >
          {activities.map((activity) => (
            <ListItem
              key={activity.id}
              sx={{
                px: 0,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': {
                  borderBottom: 'none',
                },
              }}
            >
              <ListItemAvatar>
                {activity.user.picture ? (
                  <Avatar src={activity.user.picture} alt={activity.user.name} />
                ) : (
                  <Avatar
                    sx={{
                      bgcolor: getActivityColor(activity.type),
                    }}
                  >
                    {getInitials(activity.user.name)}
                  </Avatar>
                )}
              </ListItemAvatar>

              <ListItemText
                primaryTypographyProps={{ component: 'div' }}
                secondaryTypographyProps={{ component: 'div' }}
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {activity.user.name}
                    </Typography>
                    <Chip
                      label={getActivityTypeLabel(activity.type)}
                      size="small"
                      icon={getActivityIcon(activity.type)}
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        backgroundColor: getActivityColor(activity.type),
                        color: '#fff',
                        '& .MuiChip-icon': {
                          color: '#fff',
                          fontSize: '0.9rem',
                        },
                      }}
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {activity.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestamp(activity.timestamp)} • {activity.user.email}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
}
